import type {
  BrandSpecials,
  MaterializedMenuProduct,
} from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { Flex, Typography } from '@iheartjane/reefer';
import { useMemo } from 'react';

import { DMMenuRow } from '@jane/bloom/components/menuRow/dmMenuRow';
import { ProductGrid } from '@jane/bloom/components/productListingPage/productGrid';
import { useSkeletonLoader } from '@jane/bloom/hooks/useSkeletonLoader';
import type { CustomLanguage } from '@jane/bloom/loaders/rootLoader';
import { formatProductCount } from '@jane/bloom/utils/strings/formatProductCount';
import type { NavigationCategory } from '@jane/bloom/viewModels/navigationCategory';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type {
  StoreSpecial,
  StoreSpecials,
} from '@jane/bloom/viewModels/schemas/storeSpecials';

type DefaultCategoryTabProps = {
  brandSpecials: BrandSpecials;
  category: NavigationCategory | undefined;
  customLanguage: CustomLanguage;
  dynamicPlacement?: SmartSort<MaterializedMenuProduct>;
  enabledSpecialsForRow: StoreSpecial[];
  janeGold: JaneGold;
  menuTopRowInstance?: SmartSort<MaterializedMenuProduct>;
  pauseRefetching: () => void;
  preloadedProductIds: number[];
  rowInstance: SmartSort<MaterializedMenuProduct> | undefined;
  specials: StoreSpecials;
  unpauseRefetching: () => void;
};

export const CategoryTab = ({
  brandSpecials,
  category,
  customLanguage,
  dynamicPlacement,
  enabledSpecialsForRow,
  janeGold,
  menuTopRowInstance,
  pauseRefetching,
  preloadedProductIds,
  rowInstance,
  specials,
  unpauseRefetching,
}: DefaultCategoryTabProps) => {
  const { skeletonLoader } = useSkeletonLoader();

  const hasResults = useMemo(
    () => (rowInstance?.products?.length ?? 0) > 0,
    [rowInstance?.products?.length]
  );

  if (!rowInstance) {
    return <></>;
  }

  if (skeletonLoader) {
    return skeletonLoader;
  }

  return (
    <main className="main">
      {hasResults && (
        <Typography color="grays-mid" className="product-count">
          {formatProductCount(rowInstance?.totalProducts)}
        </Typography>
      )}

      {!hasResults && (
        <Flex justifyContent="center" alignItems="center" height="500px">
          <Typography
            color="inherit"
            variant="h1-semibold"
            data-testid="no-results"
          >
            No Products Available
          </Typography>
        </Flex>
      )}

      {menuTopRowInstance && (
        <DMMenuRow
          pauseRefetching={pauseRefetching}
          unpauseRefetching={unpauseRefetching}
          brandSpecials={brandSpecials}
          menuTopRow={menuTopRowInstance}
          customLanguage={customLanguage}
          specials={enabledSpecialsForRow}
          janeGold={janeGold}
          embedInProductGrid={true}
        />
      )}

      <ProductGrid
        brandSpecials={brandSpecials}
        customLanguage={customLanguage}
        dynamicPlacement={dynamicPlacement}
        key={category}
        preloadedProductIds={preloadedProductIds}
        rowInstance={rowInstance}
        specials={specials}
        janeGold={janeGold}
      />
    </main>
  );
};
